<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { AlertCircle, Check, CheckCircle2, ExternalLink, Eye, EyeOff, History, KeyRound, PlugZap, Save, ShieldCheck } from "lucide-vue-next";
import { api } from "../services/api";
import type { ArkServiceConfig, RealtimeAuthMode, RealtimeServiceConfig, ServiceConnectionLog } from "../types";

const props = defineProps<{ onConfigChange: () => Promise<void> | void }>();
const arkConfig = ref<ArkServiceConfig>();
const realtimeConfig = ref<RealtimeServiceConfig>();
const connectionLogs = ref<ServiceConnectionLog[]>([]);
const arkDraft = ref({ apiKey: "", baseUrl: "https://ark.cn-beijing.volces.com/api/v3", model: "" });
const realtimeDraft = ref<{ authMode: RealtimeAuthMode; apiKey: string; appId: string; accessKey: string }>({
  authMode: "API_KEY", apiKey: "", appId: "", accessKey: "",
});
const loading = ref(true);
const busy = ref<"" | "save-ark" | "test-ark" | "save-realtime" | "test-realtime">("");
const showArkSecret = ref(false);
const showRealtimeSecret = ref(false);
const arkMessage = ref("");
const arkError = ref("");
const realtimeMessage = ref("");
const realtimeError = ref("");

const canSaveArk = computed(() => Boolean(arkDraft.value.baseUrl.trim() && (arkDraft.value.apiKey.trim() || arkConfig.value?.apiKeyConfigured)));
const canSaveRealtime = computed(() => realtimeDraft.value.authMode === "API_KEY"
  ? Boolean(realtimeDraft.value.apiKey.trim() || realtimeConfig.value?.apiKeyConfigured)
  : Boolean(realtimeDraft.value.appId.trim() && (realtimeDraft.value.accessKey.trim() || realtimeConfig.value?.accessKeyConfigured)));

async function load(): Promise<void> {
  loading.value = true;
  try {
    const [ark, realtime, logs] = await Promise.all([
      api.arkServiceConfig(), api.realtimeServiceConfig(), api.realtimeServiceConfigLogs(),
    ]);
    arkConfig.value = ark;
    realtimeConfig.value = realtime;
    connectionLogs.value = logs;
    arkDraft.value.baseUrl = ark.baseUrl;
    realtimeDraft.value.authMode = realtime.authMode;
    realtimeDraft.value.appId = realtime.appId;
  } finally {
    loading.value = false;
  }
}

async function saveArk(): Promise<void> {
  busy.value = "save-ark";
  arkError.value = "";
  arkMessage.value = "";
  try {
    const input: { apiKey?: string; baseUrl: string } = { baseUrl: arkDraft.value.baseUrl.trim() };
    if (arkDraft.value.apiKey.trim()) input.apiKey = arkDraft.value.apiKey.trim();
    arkConfig.value = await api.saveArkServiceConfig(input);
    arkDraft.value.apiKey = "";
    arkMessage.value = "方舟配置已加密保存并立即生效";
    await props.onConfigChange();
  } catch (reason) {
    arkError.value = reason instanceof Error ? reason.message : "保存失败";
  } finally {
    busy.value = "";
  }
}

async function testArk(): Promise<void> {
  busy.value = "test-ark";
  arkError.value = "";
  arkMessage.value = "";
  try {
    const result = await api.testArkServiceConfig(arkDraft.value.model.trim() || undefined);
    arkMessage.value = `${result.message}（${result.model}，${result.durationMs} ms）`;
    await props.onConfigChange();
  } catch (reason) {
    arkError.value = reason instanceof Error ? reason.message : "连接检测失败";
  } finally {
    busy.value = "";
  }
}

async function saveRealtime(): Promise<void> {
  busy.value = "save-realtime";
  realtimeError.value = "";
  realtimeMessage.value = "";
  try {
    if (realtimeDraft.value.authMode === "API_KEY") {
      const input: { authMode: "API_KEY"; apiKey?: string } = { authMode: "API_KEY" };
      if (realtimeDraft.value.apiKey.trim()) input.apiKey = realtimeDraft.value.apiKey.trim();
      realtimeConfig.value = await api.saveRealtimeServiceConfig(input);
      realtimeDraft.value.apiKey = "";
    } else {
      const input: { authMode: "APP_ID_ACCESS_TOKEN"; appId: string; accessKey?: string } = {
        authMode: "APP_ID_ACCESS_TOKEN", appId: realtimeDraft.value.appId.trim(),
      };
      if (realtimeDraft.value.accessKey.trim()) input.accessKey = realtimeDraft.value.accessKey.trim();
      realtimeConfig.value = await api.saveRealtimeServiceConfig(input);
      realtimeDraft.value.accessKey = "";
    }
    realtimeMessage.value = "实时语音配置已加密保存并立即生效";
    await props.onConfigChange();
  } catch (reason) {
    realtimeError.value = reason instanceof Error ? reason.message : "保存失败";
  } finally {
    busy.value = "";
  }
}

async function testRealtime(): Promise<void> {
  busy.value = "test-realtime";
  realtimeError.value = "";
  realtimeMessage.value = "";
  try {
    const result = await api.testRealtimeServiceConfig();
    realtimeMessage.value = result.message;
    await load();
    await props.onConfigChange();
  } catch (reason) {
    realtimeError.value = reason instanceof Error ? reason.message : "连接检测失败";
    await load();
  } finally {
    busy.value = "";
  }
}

onMounted(() => void load());
</script>

<template>
  <div class="view-stack service-config-page">
    <section class="service-config-hero">
      <div class="service-config-icon"><PlugZap :size="24" /></div>
      <div class="service-config-intro">
        <span class="eyebrow">VOLCENGINE SERVICES</span>
        <h2>AI 服务配置</h2>
        <p>方舟文字模型与豆包实时语音使用独立凭证。凭证只在后台加密保存，客户端不会获得明文。</p>
      </div>
      <div :class="['config-status-pill', { ready: arkConfig?.ready && realtimeConfig?.ready }]">
        <CheckCircle2 v-if="arkConfig?.ready && realtimeConfig?.ready" :size="15" /><AlertCircle v-else :size="15" />
        {{ arkConfig?.ready && realtimeConfig?.ready ? "服务已配置" : "存在未配置服务" }}
      </div>
    </section>

    <div class="config-layout">
      <section class="surface-section config-form-card">
        <div class="section-header">
          <div><span class="eyebrow">ARK RESPONSES API</span><h3>方舟文字模型与 Function Calling</h3></div>
          <span :class="['status-dot', arkConfig?.ready ? 'ok' : 'bad']" />
        </div>
        <form class="form-stack" @submit.prevent="saveArk">
          <label class="form-field">
            <span>方舟 API Key</span>
            <div class="secret-input">
              <input v-model="arkDraft.apiKey" :type="showArkSecret ? 'text' : 'password'" :placeholder="arkConfig?.apiKeyConfigured ? `已保存（尾号 ${arkConfig.apiKeyHint}），留空表示不修改` : '填写方舟控制台 API Key'" autocomplete="new-password" />
              <button type="button" :title="showArkSecret ? '隐藏' : '显示'" @click="showArkSecret = !showArkSecret"><EyeOff v-if="showArkSecret" :size="17" /><Eye v-else :size="17" /></button>
            </div>
          </label>
          <label class="form-field"><span>API 地址</span><input v-model="arkDraft.baseUrl" /></label>
          <label class="form-field"><span>检测用 Endpoint ID（可选）</span><input v-model="arkDraft.model" placeholder="留空时使用最近配置的文字智能体" /><small>填写方舟在线推理 Endpoint ID，不是模型展示名称。</small></label>
          <div class="credential-security-note"><ShieldCheck :size="18" /><div><strong>设备工具边界</strong><span>模型只能请求后台工具；后台仍会检查字段白名单、设备在线状态和 MQTT ACK。</span></div></div>
          <p v-if="arkMessage" class="form-success"><Check :size="16" />{{ arkMessage }}</p>
          <p v-if="arkError" class="form-error"><AlertCircle :size="16" />{{ arkError }}</p>
          <div class="config-actions">
            <button class="button secondary" type="button" :disabled="!arkConfig?.ready || Boolean(busy)" @click="testArk"><PlugZap :size="16" />{{ busy === "test-ark" ? "检测中..." : "检测连接" }}</button>
            <button class="button primary" type="submit" :disabled="loading || !canSaveArk || Boolean(busy)"><Save :size="16" />{{ busy === "save-ark" ? "保存中..." : "保存方舟配置" }}</button>
          </div>
        </form>
      </section>

      <section class="surface-section config-form-card">
        <div class="section-header">
          <div><span class="eyebrow">DOUBAO REALTIME</span><h3>端到端实时语音</h3></div>
          <span :class="['status-dot', realtimeConfig?.ready ? 'ok' : 'bad']" />
        </div>
        <form class="form-stack" @submit.prevent="saveRealtime">
          <div class="form-field">
            <span>认证方式</span>
            <div class="auth-mode-switch" role="group" aria-label="实时语音认证方式">
              <button type="button" :class="{ active: realtimeDraft.authMode === 'API_KEY' }" @click="realtimeDraft.authMode = 'API_KEY'">新版 API Key</button>
              <button type="button" :class="{ active: realtimeDraft.authMode === 'APP_ID_ACCESS_TOKEN' }" @click="realtimeDraft.authMode = 'APP_ID_ACCESS_TOKEN'">旧版 App ID</button>
            </div>
          </div>
          <label v-if="realtimeDraft.authMode === 'API_KEY'" class="form-field">
            <span>实时语音 API Key</span>
            <div class="secret-input">
              <input v-model="realtimeDraft.apiKey" :type="showRealtimeSecret ? 'text' : 'password'" :placeholder="realtimeConfig?.apiKeyConfigured ? `已保存（尾号 ${realtimeConfig.apiKeyHint}），留空表示不修改` : '填写语音控制台 API Key'" autocomplete="new-password" />
              <button type="button" :title="showRealtimeSecret ? '隐藏' : '显示'" @click="showRealtimeSecret = !showRealtimeSecret"><EyeOff v-if="showRealtimeSecret" :size="17" /><Eye v-else :size="17" /></button>
            </div>
          </label>
          <template v-else>
            <label class="form-field"><span>App ID</span><input v-model="realtimeDraft.appId" /></label>
            <label class="form-field"><span>Access Token</span><div class="secret-input"><input v-model="realtimeDraft.accessKey" :type="showRealtimeSecret ? 'text' : 'password'" :placeholder="realtimeConfig?.accessKeyConfigured ? `已保存（尾号 ${realtimeConfig.accessKeyHint}），留空表示不修改` : '填写 Access Token'" /><button type="button" :title="showRealtimeSecret ? '隐藏' : '显示'" @click="showRealtimeSecret = !showRealtimeSecret"><EyeOff v-if="showRealtimeSecret" :size="17" /><Eye v-else :size="17" /></button></div></label>
          </template>
          <dl class="config-detail-list">
            <div><dt>Resource ID</dt><dd>{{ realtimeConfig?.resourceId || "volc.speech.dialog" }}</dd></div>
            <div><dt>WebSocket</dt><dd>{{ realtimeConfig?.url || "wss://openspeech.bytedance.com/api/v3/realtime/dialogue" }}</dd></div>
          </dl>
          <p v-if="realtimeMessage" class="form-success"><Check :size="16" />{{ realtimeMessage }}</p>
          <p v-if="realtimeError" class="form-error"><AlertCircle :size="16" />{{ realtimeError }}</p>
          <div class="config-actions">
            <button class="button secondary" type="button" :disabled="!realtimeConfig?.ready || Boolean(busy)" @click="testRealtime"><PlugZap :size="16" />{{ busy === "test-realtime" ? "检测中..." : "检测连接" }}</button>
            <button class="button primary" type="submit" :disabled="loading || !canSaveRealtime || Boolean(busy)"><Save :size="16" />{{ busy === "save-realtime" ? "保存中..." : "保存实时语音配置" }}</button>
          </div>
        </form>
      </section>
    </div>

    <section class="surface-section connection-log-section">
      <div class="section-header slim"><div><span class="eyebrow">REALTIME TESTS</span><h3>实时语音连接日志</h3></div><History :size="18" /></div>
      <div v-if="connectionLogs.length" class="connection-log-list">
        <article v-for="item in connectionLogs" :key="item.id" :class="['connection-log-row', item.status]">
          <span class="connection-log-state"><CheckCircle2 v-if="item.status === 'success'" :size="15" /><AlertCircle v-else :size="15" /></span>
          <div><strong>{{ item.status === "success" ? "连接成功" : "连接失败" }}</strong><p>{{ item.message }}</p><small>Trace ID：{{ item.traceId }}<template v-if="item.providerLogId"> · Log ID：{{ item.providerLogId }}</template></small></div>
          <div class="connection-log-meta"><time>{{ new Date(item.createdAt).toLocaleString("zh-CN") }}</time><span>{{ item.durationMs }} ms</span></div>
        </article>
      </div>
      <div v-else class="empty-state compact-empty">暂无连接检测记录</div>
      <a class="button secondary" href="https://www.volcengine.com/docs/82379/1958524" target="_blank" rel="noreferrer">方舟 Function Calling 官方文档 <ExternalLink :size="14" /></a>
    </section>
  </div>
</template>
